import moment from "moment";

// ── Readiness Index (0-100) ───────────────────────────────────────────────────
// Combina TQR + RPE + stato infortunio per determinare quanto un giocatore
// e pronto a giocare. Usato nella Dashboard e nel Decision Brief.

export function computeReadinessIndex({avgTQR, avgRPE, isInjured}) {
	if (isInjured) return {value: 0, label: "Infortunato", color: "red"};
	if (avgTQR === null && avgRPE === null)
		return {value: null, label: "N/D", color: "gray"};

	// TQR: scala 6-20, normalizzo a 0-100
	const tqrScore =
		avgTQR !== null
			? Math.min(100, Math.max(0, ((avgTQR - 6) / 14) * 100))
			: null;

	// RPE: scala 1-10, invertita (1 = meglio = 100, 10 = peggio = 0)
	const rpeScore =
		avgRPE !== null
			? Math.min(100, Math.max(0, ((10 - avgRPE) / 9) * 100))
			: null;

	let score;
	if (tqrScore !== null && rpeScore !== null) {
		score = Math.round(tqrScore * 0.6 + rpeScore * 0.4);
	} else if (tqrScore !== null) {
		score = Math.round(tqrScore);
	} else {
		score = Math.round(rpeScore);
	}

	const label =
		score >= 75
			? "Ottimo"
			: score >= 50
				? "Discreto"
				: score >= 25
					? "Basso"
					: "Critico";
	const color = score >= 75 ? "green" : score >= 50 ? "yellow" : "red";

	return {value: score, label, color};
}

// ── Performance Index da dati reali (0-100) ───────────────────────────────────
// Per outfield: basato su gol/90, assist/90, presenze, minuti
// Per portieri: basato sui 6 KPI calcolati dai report gara

export function computePerformanceIndex({realStats}) {
	if (!realStats) return null;

	if (realStats.isGoalkeeper) {
		return computeGoalkeeperPerformance(realStats.goalkeeper);
	}

	if (realStats.outfield) {
		return computeOutfieldPerformance(realStats.outfield);
	}

	return null;
}

function computeOutfieldPerformance(stats) {
	if (!stats || !stats.matchesPlayed) return null;

	const metrics = [];

	// Gol per-90 (benchmark: 0 = 0, 0.5+ = 100 per attaccanti, scala adattiva)
	if (stats.goalsPer90 != null) {
		const goalsPer90 = stats.goalsPer90;
		const goalScore = Math.min(100, (goalsPer90 / 0.4) * 100);
		metrics.push({
			code: "GOL/90",
			label: "Gol per 90'",
			value: goalsPer90,
			normalized: Math.round(goalScore),
			weight: 20,
		});
	}

	// Assist per-90
	if (stats.assistsPer90 != null) {
		const assistsPer90 = stats.assistsPer90;
		const assistScore = Math.min(100, (assistsPer90 / 0.3) * 100);
		metrics.push({
			code: "ASS/90",
			label: "Assist per 90'",
			value: assistsPer90,
			normalized: Math.round(assistScore),
			weight: 15,
		});
	}

	// Minuti per partita (continuita)
	if (stats.minutesPerMatch != null) {
		const mpm = stats.minutesPerMatch;
		const continuityScore = Math.min(100, (mpm / 90) * 100);
		metrics.push({
			code: "MIN/G",
			label: "Minuti per gara",
			value: mpm,
			normalized: Math.round(continuityScore),
			weight: 25,
		});
	}

	// Disciplina (inversamente proporzionale ai cartellini)
	if (stats.cardsPer90 != null) {
		const disciplineScore = Math.max(0, 100 - stats.cardsPer90 * 200);
		metrics.push({
			code: "DISC",
			label: "Disciplina",
			value: stats.cardsPer90,
			normalized: Math.round(disciplineScore),
			weight: 10,
		});
	}
	if (!metrics.length) return null;
	if (stats.matchesAvailable > 0) {
		metrics.push({
			code: "PRES",
			label: "Presenze",
			value: stats.matchesPlayed,
			normalized: Math.min(
				100,
				(stats.matchesPlayed / stats.matchesAvailable) * 100,
			),
			weight: 15,
		});
	}

	if (!metrics.length) return null;

	const totalWeight = metrics.reduce((s, m) => s + m.weight, 0);
	const score = Math.round(
		metrics.reduce((s, m) => s + m.normalized * m.weight, 0) / totalWeight,
	);
	const clampedScore = Math.min(100, Math.max(0, score));

	return {
		value: clampedScore,
		label:
			clampedScore >= 75
				? "Eccellente"
				: clampedScore >= 55
					? "Buono"
					: clampedScore >= 35
						? "Nella media"
						: "Da migliorare",
		color: clampedScore >= 75 ? "green" : clampedScore >= 55 ? "yellow" : "red",
		type: "outfield",
		details: metrics,
	};
}

function computeGoalkeeperPerformance(gkStats) {
	if (!gkStats?.kpis) return null;

	const kpis = gkStats.kpis;
	const metrics = [];

	// Shot stopping (benchmark: 0.6-1.0)
	if (kpis.shotStoppingRatio != null) {
		const score = Math.min(
			100,
			Math.max(0, ((kpis.shotStoppingRatio - 0.4) / 0.5) * 100),
		);
		metrics.push({
			code: "PARA",
			label: "Indice parate",
			value: kpis.shotStoppingRatio,
			normalized: Math.round(score),
			weight: 25,
		});
	}

	// Distribution (benchmark: 0.5-1.0)
	if (kpis.distributionRatio != null) {
		const score = Math.min(
			100,
			Math.max(0, ((kpis.distributionRatio - 0.4) / 0.5) * 100),
		);
		metrics.push({
			code: "DISTR",
			label: "Distribuzione",
			value: kpis.distributionRatio,
			normalized: Math.round(score),
			weight: 20,
		});
	}

	// Aerial control (benchmark: 0.5-1.0)
	if (kpis.aerialControlRatio != null) {
		const score = Math.min(
			100,
			Math.max(0, ((kpis.aerialControlRatio - 0.4) / 0.5) * 100),
		);
		metrics.push({
			code: "AEREO",
			label: "Controllo aereo",
			value: kpis.aerialControlRatio,
			normalized: Math.round(score),
			weight: 15,
		});
	}

	// Set pieces (benchmark: 0.7-1.0)
	if (kpis.setPieceControlRatio != null) {
		const score = Math.min(
			100,
			Math.max(0, ((kpis.setPieceControlRatio - 0.6) / 0.35) * 100),
		);
		metrics.push({
			code: "PIAZZ",
			label: "Palle inattive",
			value: kpis.setPieceControlRatio,
			normalized: Math.round(score),
			weight: 15,
		});
	}

	// Error impact (lower is better, benchmark: 0.0-0.4)
	if (kpis.errorImpactRatio != null) {
		const score = Math.min(
			100,
			Math.max(0, ((0.5 - kpis.errorImpactRatio) / 0.5) * 100),
		);
		metrics.push({
			code: "ERRORI",
			label: "Pulizia errori",
			value: kpis.errorImpactRatio,
			normalized: Math.round(score),
			weight: 25,
		});
	}

	if (!metrics.length) return null;

	const totalWeight = metrics.reduce((s, m) => s + m.weight, 0);
	const score = Math.round(
		metrics.reduce((s, m) => s + m.normalized * m.weight, 0) / totalWeight,
	);
	const clampedScore = Math.min(100, Math.max(0, score));

	return {
		value: clampedScore,
		label:
			clampedScore >= 75
				? "Eccellente"
				: clampedScore >= 55
					? "Buono"
					: clampedScore >= 35
						? "Nella media"
						: "Da migliorare",
		color: clampedScore >= 75 ? "green" : clampedScore >= 55 ? "yellow" : "red",
		type: "goalkeeper",
		details: metrics,
		reportCount: gkStats.reportCount,
	};
}

// ── Availability Index (0-100) ────────────────────────────────────────────────

export function computeAvailabilityIndex({injuries, seasonStart, seasonEnd}) {
	if (!seasonStart || !seasonEnd) return null;

	const totalDays = moment(seasonEnd).diff(moment(seasonStart), "days");
	if (totalDays <= 0) return null;

	const daysLost = (injuries || []).reduce((sum, inj) => {
		const start = inj.startDate || inj.injuryDate;
		if (!start) return sum;
		const end = inj.sportsDischargeDate
			? moment(inj.sportsDischargeDate)
			: moment();
		const injStart = moment(start);
		const effectiveStart = moment.max(injStart, moment(seasonStart));
		const effectiveEnd = moment.min(end, moment(seasonEnd));
		return sum + Math.max(0, effectiveEnd.diff(effectiveStart, "days"));
	}, 0);

	const availability = Math.round(((totalDays - daysLost) / totalDays) * 100);
	const score = Math.min(100, Math.max(0, availability));

	return {
		value: score,
		daysLost,
		label:
			score >= 90
				? "Sempre disponibile"
				: score >= 70
					? "Buona disponibilita"
					: score >= 50
						? "Discontinuo"
						: "Spesso assente",
		color: score >= 90 ? "green" : score >= 70 ? "yellow" : "red",
	};
}

// ── Valuation Trend ───────────────────────────────────────────────────────────

export function computeValuationTrend(valuations) {
	if (!valuations?.length) return null;

	const sorted = [...valuations]
		.filter((v) => v.finalOverallRating || v.averageRating || v.rating)
		.sort(
			(a, b) =>
				new Date(a.date || a.createdAt) - new Date(b.date || b.createdAt),
		);

	if (sorted.length < 1) return null;

	const ratings = sorted
		.map((v) => parseFloat(v.finalOverallRating || v.averageRating || v.rating))
		.filter((v) => !isNaN(v));

	if (!ratings.length) return null;

	const latest = ratings[ratings.length - 1];
	const avg = ratings.reduce((s, v) => s + v, 0) / ratings.length;
	const trend = ratings.length >= 2 ? latest - ratings[0] : 0;

	return {
		latest: Math.round(latest * 10) / 10,
		average: Math.round(avg * 10) / 10,
		trend: Math.round(trend * 10) / 10,
		count: ratings.length,
		label: trend > 0.5 ? "In crescita" : trend < -0.5 ? "In calo" : "Stabile",
		color: trend > 0.5 ? "green" : trend < -0.5 ? "red" : "yellow",
	};
}

// ── Player Summary ────────────────────────────────────────────────────────────

export function computePlayerSummary({
	playerData,
	avgTQR = null,
	avgRPE = null,
	isInjured = false,
	injuries = [],
	valuations = [],
	realStats = null,
	seasonStart = null,
	seasonEnd = null,
}) {
	return {
		readiness: computeReadinessIndex({avgTQR, avgRPE, isInjured}),
		performance: computePerformanceIndex({realStats}),
		availability: computeAvailabilityIndex({injuries, seasonStart, seasonEnd}),
		valuationTrend: computeValuationTrend(valuations),
	};
}
